import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { setImpersonationSession } from '../utils/authSession';

interface ImpersonationPayload {
  token: string;
  user: {
    id?: string;
    email?: string;
    name?: string;
    role?: string;
    isAdmin?: boolean;
    isSuperAdmin?: boolean;
  };
  domain: string;
  targetEmail: string;
  targetName?: string;
  redirectPath?: string;
  returnPath?: string;
  returnLabel?: string;
  actorRole?: 'admin' | 'superadmin';
}

const decodePayload = (encoded: string): ImpersonationPayload | null => {
  try {
    const json = window.atob(encoded);
    const parsed = JSON.parse(json) as ImpersonationPayload;
    if (!parsed?.token || !parsed?.user?.email || !parsed?.domain) return null;
    return parsed;
  } catch {
    return null;
  }
};

const AdminImpersonatePage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const payloadRaw = searchParams.get('payload');
    if (!payloadRaw) {
      setError('Missing impersonation payload.');
      return;
    }
    const payload = decodePayload(payloadRaw);
    if (!payload) {
      setError('Invalid impersonation payload.');
      return;
    }

    setImpersonationSession({
      token: payload.token,
      user: payload.user,
      domain: payload.domain,
      targetEmail: payload.targetEmail || payload.user.email || '',
      targetName: payload.targetName || payload.user.name || '',
      returnPath: payload.returnPath,
      returnLabel: payload.returnLabel,
      actorRole: payload.actorRole
    });
    navigate(payload.redirectPath || '/admin/settings', { replace: true });
  }, [navigate, searchParams]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
      <div className="bg-white rounded-xl shadow-md p-8 max-w-lg text-center">
        <h1 className="text-2xl font-bold text-gray-800 mb-3">Opening Admin Portal...</h1>
        <p className="text-gray-600">
          {error || 'Setting up impersonation session.'}
        </p>
        {error && (
          <button
            type="button"
            onClick={() => navigate('/admin/users')}
            style={{
              marginTop: '14px',
              padding: '8px 16px',
              border: 'none',
              borderRadius: '8px',
              backgroundColor: '#2563eb',
              color: 'white',
              fontWeight: 700
            }}
          >
            Back to Users
          </button>
        )}
      </div>
    </div>
  );
};

export default AdminImpersonatePage;
